import * as React from "react";
import * as _ from "underscore";

import Value from "./Value";
import { IWorksheetState } from "./interfaces";
import { getFieldNames } from "./wsDefs";
import { getWsById } from "./wsHelpers";

const humanize = require("underscore.string/humanize");
const styles = require("./styles.module.scss");

const TableHead: React.SFC<{
  fieldNames: string[];
}> = ({ fieldNames }) => (
  <thead>
    <tr>
      {fieldNames.map(field => (
        <th key={field}>{humanize(field)}</th>
      ))}
      <th />
    </tr>
  </thead>
);

const TableBody: React.SFC<{
  worksheets: IWorksheetState[];
  wsIds: string[];
  fieldNames: string[];
  onUpdate: Function;
  setCurrentWsId: Function;
  onClickDelete: Function;
  addWsToParent: Function;
  deleteWsFromParent: Function;
  showHidden?: boolean;
  readOnly?: boolean;
}> = props => (
  <tbody>
    {props.wsIds.map(wsId => (
      <TableRow key={wsId} wsId={wsId} {...props} />
    ))}
  </tbody>
);

const TableRow: React.SFC<{
  worksheets: IWorksheetState[];
  wsId: string;
  fieldNames: string[];
  onUpdate: Function;
  setCurrentWsId: Function;
  onClickDelete: Function;
  addWsToParent: Function;
  deleteWsFromParent: Function;
  showHidden?: boolean;
  readOnly?: boolean;
}> = ({
  worksheets,
  wsId,
  fieldNames,
  onUpdate,
  setCurrentWsId,
  onClickDelete,
  addWsToParent,
  deleteWsFromParent,
  showHidden,
  readOnly,
}) => {
  const ws = getWsById(worksheets, wsId);
  if (!ws) {
    throw new Error(`WorksheetSliceAsTable failed: cannot find ${wsId}`);
  }

  return (
    <tr>
      {fieldNames.map(field => (
        <td key={field} className={field}>
          <Value
            worksheets={worksheets}
            ws={ws}
            field={field}
            onUpdate={onUpdate}
            setCurrentWsId={setCurrentWsId}
            addWsToParent={addWsToParent}
            deleteWsFromParent={deleteWsFromParent}
            showHidden={showHidden}
            readOnly={readOnly}
          />
        </td>
      ))}
      <td>
        <div className={styles.wsSlice__buttons}>
          {!readOnly && (
            <div onClick={() => onClickDelete(wsId)} className={`${styles.wsSlice__button}`}>
              <i className="fa fa-fw fa-minus-circle" />
            </div>
          )}
          <div onClick={() => setCurrentWsId(wsId)} className={`${styles.wsSlice__button}`}>
            <i className="fa fa-fw fa-external-link " />
          </div>
        </div>
      </td>
    </tr>
  );
};

const WorksheetSliceAsTable: React.SFC<{
  worksheets: IWorksheetState[];
  wsName: string;
  wsIds: string[];
  onClickLink: Function;
  onClickAdd: any;
  onClickDelete: Function;
  onUpdate: Function;
  setCurrentWsId: Function;
  addWsToParent: Function;
  deleteWsFromParent: Function;
  showHidden?: boolean;
  readOnly?: boolean;
}> = ({
  worksheets,
  wsName,
  wsIds,
  onClickLink,
  onClickAdd,
  onClickDelete,
  onUpdate,
  setCurrentWsId,
  addWsToParent,
  deleteWsFromParent,
  showHidden,
  readOnly,
}) => {
  const fieldNames = _.reject(getFieldNames(wsName), f => f === "id" || f === "version");

  const confirmAndDelete = wsId => {
    if (confirm("Are you sure you want to delete this row?")) {
      onClickDelete(wsId);
    }
  };

  return (
    <div>
      <table className={`table table-striped table-hover ${styles.wsSlice__table}`}>
        <TableHead fieldNames={fieldNames} />
        <TableBody
          worksheets={worksheets}
          wsIds={wsIds}
          fieldNames={fieldNames}
          onUpdate={onUpdate}
          setCurrentWsId={setCurrentWsId}
          onClickDelete={confirmAndDelete}
          addWsToParent={addWsToParent}
          deleteWsFromParent={deleteWsFromParent}
          showHidden={showHidden}
          readOnly={readOnly}
        />
      </table>
      {!readOnly && (
        <div onClick={onClickAdd} className={`${styles.wsSlice__button} ${styles.wsSlice__add}`}>
          <i className="fa fa-plus-circle" />
        </div>
      )}
    </div>
  );
};

export default WorksheetSliceAsTable;
